import React, { useEffect, useRef, useState } from "react"

// Explorateur d'appartements façon Apple (« choisissez votre modèle ») :
// une coupe de l'immeuble sert de sélecteur d'étage, à côté un grand visuel
// en fondu croisé (les deux suites réunies), les chiffres en grand et la
// composition. Tous les panneaux sont rendus dans le HTML (SEO intact) et
// superposés dans la même cellule de grille : seul l'actif est visible.
// Mobile : contrôle segmenté + balayage du visuel. prefers-reduced-motion :
// pas d'animation de chiffres ni de zoom.
//
// items: [{ key, name, floor, floorShort, suites:[nomA, nomB], imgs:[a, b],
//           size, sizeNum, bedrooms, baths, guests, note, plans?:[a, b?] }]
// labels: { eyebrow, title, sub, sqm, bedrooms, baths, guests, door, cta, caption,
//           viewPhotos?, viewPlan?, plan? }
// plans (optionnel) : plan(s) officiel(s) de l'item ; s'il y en a, une bascule
// « Photos / Plan » apparaît sur le visuel (plan en entier, fond clair).

const STYLE = `
  .ax-wrap { background:#000; color:#f5f5f7; padding:96px 0 104px; overflow:hidden; }
  .ax-inner { max-width:1180px; margin:0 auto; padding:0 24px; }
  .ax-eyebrow { font-size:12px; letter-spacing:0.22em; text-transform:uppercase; color:rgba(245,245,247,0.55); display:block; margin-bottom:14px; }
  .ax-title { font-size:clamp(32px,4.6vw,60px); font-weight:600; letter-spacing:-0.045em; line-height:1.04; margin:0 0 18px; color:#f5f5f7; }
  .ax-sub { font-size:clamp(17px,1.5vw,21px); line-height:1.5; color:rgba(245,245,247,0.68); max-width:760px; margin:0 0 48px; }
  .ax-seg { display:none; gap:4px; padding:4px; border-radius:999px; background:rgba(255,255,255,0.08); margin-bottom:20px; }
  .ax-seg button { flex:1; min-height:40px; border:0; border-radius:999px; background:transparent; color:rgba(245,245,247,0.7); font:inherit; font-size:15px; font-weight:500; cursor:pointer; transition:background 260ms, color 260ms; }
  .ax-seg button[aria-pressed="true"] { background:#f5f5f7; color:#1d1d1f; }
  .ax-grid { display:grid; grid-template-columns:340px 1fr; gap:44px; align-items:start; }
  .ax-house { display:flex; flex-direction:column; gap:8px; }
  .ax-roof { height:34px; margin:0 18px; background:linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.04)); clip-path:polygon(6% 100%,94% 100%,82% 0,18% 0); }
  .ax-floor { position:relative; display:flex; align-items:center; justify-content:space-between; gap:12px; width:100%; min-height:86px; padding:0 20px; border:1px solid rgba(255,255,255,0.10); border-radius:18px; background:rgba(255,255,255,0.03); color:rgba(245,245,247,0.62); font:inherit; text-align:left; cursor:pointer; transition:background 320ms cubic-bezier(0.22,1,0.36,1), border-color 320ms, color 320ms, transform 320ms cubic-bezier(0.22,1,0.36,1); }
  .ax-floor:hover { background:rgba(255,255,255,0.07); color:#f5f5f7; }
  .ax-floor[aria-pressed="true"] { background:rgba(0,113,227,0.16); border-color:rgba(41,151,255,0.85); color:#fff; transform:scale(1.02); }
  .ax-floor-name { font-size:17px; font-weight:600; letter-spacing:-0.02em; }
  .ax-floor-meta { font-size:13px; color:inherit; opacity:0.75; margin-top:3px; white-space:nowrap; }
  .ax-floor-size { font-size:15px; font-weight:500; white-space:nowrap; }
  .ax-windows { position:absolute; right:20px; top:12px; display:none; }
  .ax-ground { height:10px; margin:2px 6px 0; border-radius:4px; background:rgba(255,255,255,0.06); }
  .ax-ground-label { font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:rgba(245,245,247,0.38); text-align:center; margin-top:10px; }
  .ax-stage { display:grid; }
  .ax-panel { grid-area:1 / 1; opacity:0; visibility:hidden; transition:opacity 520ms cubic-bezier(0.22,1,0.36,1), visibility 0s linear 520ms; }
  .ax-panel.is-on { opacity:1; visibility:visible; transition:opacity 620ms cubic-bezier(0.22,1,0.36,1), visibility 0s; }
  .ax-media { position:relative; border-radius:28px; overflow:hidden; aspect-ratio:16/9; background:#111; touch-action:pan-y; }
  .ax-media img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; transform:scale(1.06); transition:transform 1400ms cubic-bezier(0.22,1,0.36,1), opacity 700ms ease; }
  .ax-panel.is-on .ax-media img { transform:scale(1); }
  .ax-media img.ax-b { opacity:0; }
  .ax-media.show-b img.ax-b { opacity:1; }
  .ax-media-scrim { position:absolute; inset:0; background:linear-gradient(180deg,rgba(0,0,0,0) 45%,rgba(0,0,0,0.62) 100%); pointer-events:none; }
  .ax-suites { position:absolute; left:20px; right:20px; bottom:18px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .ax-chip { border:0; font:inherit; font-size:14px; font-weight:500; padding:8px 14px; border-radius:999px; background:rgba(255,255,255,0.16); color:#fff; backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); cursor:pointer; transition:background 240ms, color 240ms; }
  .ax-chip[aria-pressed="true"] { background:#f5f5f7; color:#1d1d1f; }
  .ax-suites, .ax-media-scrim { transition:opacity 320ms ease; }
  .ax-media.show-plan .ax-suites, .ax-media.show-plan .ax-media-scrim { opacity:0; pointer-events:none; }
  .ax-plan { position:absolute; inset:0; z-index:2; display:flex; align-items:stretch; justify-content:center; gap:12px; padding:18px 18px 18px; background:#fff; opacity:0; visibility:hidden; transition:opacity 420ms cubic-bezier(0.22,1,0.36,1), visibility 0s linear 420ms; }
  .ax-media.show-plan .ax-plan { opacity:1; visibility:visible; transition:opacity 420ms cubic-bezier(0.22,1,0.36,1), visibility 0s; }
  .ax-media .ax-plan img { position:static; flex:1 1 0; min-width:0; width:auto; height:100%; object-fit:contain; transform:none; opacity:1; transition:none; }
  .ax-view { position:absolute; top:16px; right:16px; z-index:3; display:flex; gap:2px; padding:3px; border-radius:999px; background:rgba(29,29,31,0.58); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); }
  .ax-view button { min-height:32px; padding:0 14px; border:0; border-radius:999px; background:transparent; color:rgba(255,255,255,0.86); font:inherit; font-size:13px; font-weight:600; cursor:pointer; transition:background 240ms, color 240ms; }
  .ax-view button[aria-pressed="true"] { background:#f5f5f7; color:#1d1d1f; }
  .ax-door { display:inline-flex; align-items:center; gap:6px; font-size:12px; letter-spacing:0.12em; text-transform:uppercase; color:rgba(255,255,255,0.78); }
  .ax-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:1px; margin-top:20px; border-radius:22px; overflow:hidden; background:rgba(255,255,255,0.10); }
  .ax-stat { background:#0b0b0c; padding:18px 18px; }
  .ax-stat-num { font-size:clamp(30px,3.4vw,46px); font-weight:600; letter-spacing:-0.045em; line-height:1; color:#f5f5f7; font-variant-numeric:tabular-nums; }
  .ax-stat-num small { font-size:0.46em; font-weight:500; letter-spacing:-0.01em; margin-left:3px; color:rgba(245,245,247,0.7); }
  .ax-stat-lbl { font-size:13px; color:rgba(245,245,247,0.55); margin-top:8px; }
  .ax-note { display:flex; flex-wrap:wrap; align-items:baseline; justify-content:space-between; gap:12px; margin-top:22px; }
  .ax-note-name { font-size:22px; font-weight:600; letter-spacing:-0.03em; color:#f5f5f7; }
  .ax-note-text { font-size:16px; color:rgba(245,245,247,0.68); }
  .ax-foot { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:20px; margin-top:44px; padding-top:28px; border-top:1px solid rgba(255,255,255,0.10); }
  .ax-caption { font-size:13px; line-height:1.6; color:rgba(245,245,247,0.5); max-width:640px; margin:0; }
  .ax-caption a { color:#2997ff; text-decoration:none; }
  .ax-caption a:hover { text-decoration:underline; }
  .ax-cta { display:inline-flex; align-items:center; justify-content:center; min-height:48px; padding:0 1.6rem; border-radius:999px; border:0; background:#0071e3; color:#fff; font:inherit; font-size:17px; font-weight:500; cursor:pointer; box-shadow:0 10px 30px rgba(0,113,227,0.35); transition:transform 220ms cubic-bezier(0.22,1,0.36,1), background 220ms; }
  .ax-cta:hover { background:#0077ed; transform:translateY(-1px); }
  @media (max-width: 900px) {
    .ax-wrap { padding:72px 0 80px; }
    .ax-grid { grid-template-columns:1fr; gap:28px; }
    .ax-seg { display:flex; }
    .ax-house { display:none; }
    .ax-suites { left:14px; right:14px; bottom:14px; gap:8px; flex-wrap:nowrap; }
    .ax-chip { font-size:13px; padding:7px 11px; white-space:nowrap; min-height:40px; }
    .ax-view button { min-height:40px; }
    .ax-door-txt { display:none; }
    .ax-floor { min-height:64px; border-radius:14px; }
    .ax-floor[aria-pressed="true"] { transform:none; }
    .ax-stats { grid-template-columns:repeat(2,1fr); }
    .ax-media { border-radius:22px; aspect-ratio:4/3; }
  }
  @media (prefers-reduced-motion: reduce) {
    .ax-panel, .ax-panel.is-on, .ax-media img, .ax-floor { transition:none !important; transform:none !important; }
  }
`

const Num = ({ value, run }) => {
  const [shown, setShown] = useState(value)
  useEffect(() => {
    if (!run) return
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce || typeof value !== "number") { setShown(value); return }
    let raf
    const start = performance.now()
    const from = Math.round(value * 0.6)
    const dur = 900
    const tick = now => {
      const t = Math.min(1, (now - start) / dur)
      const e = 1 - Math.pow(1 - t, 3)
      setShown(Math.round(from + (value - from) * e))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    // failsafe : rAF peut caler (onglet en arrière-plan) — valeur finale garantie
    const safe = setTimeout(() => setShown(value), dur + 250)
    return () => { cancelAnimationFrame(raf); clearTimeout(safe) }
  }, [value, run])
  return <>{shown}</>
}

const ApartmentExplorer = ({ items, labels, onRequest, id }) => {
  const [active, setActive] = useState(0)
  const [showB, setShowB] = useState(false)
  const [view, setView] = useState("photos")
  const [seen, setSeen] = useState(false)
  const rootRef = useRef(null)
  const touch = useRef(null)

  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof IntersectionObserver === "undefined") { setSeen(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setSeen(true); io.disconnect() }
    }, { threshold: 0.25 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const select = i => { setActive((i + items.length) % items.length); setShowB(false) }

  const onTouchStart = e => { touch.current = e.touches[0].clientX }
  const onTouchEnd = e => {
    if (touch.current == null) return
    const dx = e.changedTouches[0].clientX - touch.current
    touch.current = null
    if (Math.abs(dx) < 40) return
    select(active + (dx < 0 ? 1 : -1))
  }
  const onKey = e => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); select(active + 1) }
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); select(active - 1) }
  }

  // la coupe se lit de haut (sous les toits) en bas (1er étage)
  const floorsTopDown = items.map((it, i) => ({ it, i })).reverse()

  return (
    <section id={id} className="ax-wrap" ref={rootRef}>
      <style>{STYLE}</style>
      <div className="ax-inner">
        <span className="ax-eyebrow">{labels.eyebrow}</span>
        <h2 className="ax-title" dangerouslySetInnerHTML={{ __html: labels.title }} />
        <p className="ax-sub" dangerouslySetInnerHTML={{ __html: labels.sub }} />

        <div className="ax-seg" role="group" aria-label={labels.eyebrow}>
          {items.map((it, i) => (
            <button key={it.key} type="button" aria-pressed={i === active} onClick={() => select(i)}>{it.key}</button>
          ))}
        </div>

        <div className="ax-grid">
          <div className="ax-house" role="group" aria-label={labels.eyebrow} onKeyDown={onKey}>
            <div className="ax-roof" aria-hidden="true" />
            {floorsTopDown.map(({ it, i }) => (
              <button key={it.key} type="button" className="ax-floor" aria-pressed={i === active} onClick={() => select(i)}>
                <span>
                  <span className="ax-floor-name">{it.name}</span>
                  <span className="ax-floor-meta" style={{ display:"block" }}>{it.floor} · {it.suites.join(" + ")}</span>
                </span>
                <span className="ax-floor-size">{it.size}</span>
              </button>
            ))}
            <div className="ax-ground" aria-hidden="true" />
            <div className="ax-ground-label">{labels.ground}</div>
          </div>

          <div className="ax-stage">
            {items.map((it, i) => (
              <div key={it.key} className={`ax-panel${i === active ? " is-on" : ""}`} aria-hidden={i !== active}>
                <div className={`ax-media${i === active && showB ? " show-b" : ""}${it.plans && it.plans.length && view === "plan" ? " show-plan" : ""}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                  <img src={it.imgs[0]} alt={`${it.name} — ${labels.suite} ${it.suites[0]}`} loading={i === 0 ? "eager" : "lazy"} decoding="async" />
                  <img className="ax-b" src={it.imgs[1]} alt={`${it.name} — ${labels.suite} ${it.suites[1]}`} loading="lazy" decoding="async" />
                  <div className="ax-media-scrim" />
                  {it.plans && it.plans.length ? (
                    <>
                      <div className="ax-plan" aria-hidden={view !== "plan"}>
                        {it.plans.map((src, k) => (
                          <img key={src} src={src} alt={`${labels.plan || "Plan"} — ${it.name}${it.plans.length > 1 ? ` (${it.suites[k] || k + 1})` : ""}`} loading="lazy" decoding="async" />
                        ))}
                      </div>
                      <div className="ax-view" role="group" aria-label={`${labels.viewPhotos || "Photos"} / ${labels.viewPlan || "Plan"}`}>
                        <button type="button" aria-pressed={view !== "plan"} tabIndex={i === active ? 0 : -1} onClick={() => setView("photos")}>{labels.viewPhotos || "Photos"}</button>
                        <button type="button" aria-pressed={view === "plan"} tabIndex={i === active ? 0 : -1} onClick={() => setView("plan")}>{labels.viewPlan || "Plan"}</button>
                      </div>
                    </>
                  ) : null}
                  <div className="ax-suites">
                    <button type="button" className="ax-chip" aria-pressed={!showB || i !== active} tabIndex={i === active && !(view === "plan" && it.plans && it.plans.length) ? 0 : -1} onClick={() => setShowB(false)}>{labels.suite} {it.suites[0]}</button>
                    <span className="ax-door" aria-hidden="true">
                      <svg width="14" height="16" viewBox="0 0 14 16" fill="none"><rect x="1" y="1" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/><circle cx="9.5" cy="8.5" r="1" fill="currentColor"/></svg>
                      <span className="ax-door-txt">{labels.door}</span>
                    </span>
                    <button type="button" className="ax-chip" aria-pressed={showB && i === active} tabIndex={i === active && !(view === "plan" && it.plans && it.plans.length) ? 0 : -1} onClick={() => setShowB(true)}>{labels.suite} {it.suites[1]}</button>
                  </div>
                </div>

                <div className="ax-stats">
                  <div className="ax-stat">
                    <div className="ax-stat-num">{it.sizePrefix}<Num value={it.sizeNum} run={seen && i === active} /><small>m²{it.sizeSuffix}</small></div>
                    <div className="ax-stat-lbl">{labels.sqm}</div>
                  </div>
                  <div className="ax-stat">
                    <div className="ax-stat-num"><Num value={it.bedrooms} run={seen && i === active} /></div>
                    <div className="ax-stat-lbl">{labels.bedrooms}</div>
                  </div>
                  <div className="ax-stat">
                    <div className="ax-stat-num"><Num value={it.baths} run={seen && i === active} /></div>
                    <div className="ax-stat-lbl">{labels.baths}</div>
                  </div>
                  <div className="ax-stat">
                    <div className="ax-stat-num"><Num value={it.guests} run={seen && i === active} /></div>
                    <div className="ax-stat-lbl">{labels.guests}</div>
                  </div>
                </div>

                <div className="ax-note">
                  <span className="ax-note-name">{it.name} · {it.floor}</span>
                  <span className="ax-note-text">{it.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="ax-foot">
          <p className="ax-caption">{labels.caption}</p>
          <button type="button" className="ax-cta" onClick={onRequest}>{labels.cta}</button>
        </div>
      </div>
    </section>
  )
}

export default ApartmentExplorer
